import React, { Suspense } from "react";

import { ROUTES, USER_FEATURES } from "@zing/shared/constants";
import { IHubRouteBuilder } from "./contract";

import { ProgramForm } from "@zing/shared/domain/feature/hub/company/challenge/program";
import { pageRegistry } from 'virtual:zing-registry';

// PF-2 diagnostic: surface registry contents at module load time so we can
// verify which routes the virtual module registered (check browser console).
console.log('[zing:registry] keys:', Object.keys(pageRegistry));
console.log('[zing:registry] /dashboard entry:', pageRegistry['/dashboard']);

const Page = (path: string): JSX.Element => {
  const Component = pageRegistry[path]
  if (!Component) return <div />
  return <Suspense fallback={<div />}><Component /></Suspense>
}

export const hubRoutes: IHubRouteBuilder[] = [
  {
    path: ROUTES.HUB.DASHBOARD,
    breadcrumb: "Dashboard",
    component: Page(ROUTES.HUB.DASHBOARD),
    showBreadcrumb: false,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.USER_CONFIG,
    breadcrumb: "Configurações de usuário",
    component: Page(ROUTES.HUB.USER_CONFIG),
    showBreadcrumb: false,
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.EVALUATION,
      USER_FEATURES.ADMIN_CITY_HALL,
      USER_FEATURES.CITY_HALL,
      'caixa_employee',
    ],
  },
  {
    path: ROUTES.HUB.COMPANY.CALENDAR,
    breadcrumb: "Calendário",
    component: Page(ROUTES.HUB.COMPANY.CALENDAR),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE,
    breadcrumb: "Gerenciar Espaços",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE),
    showBreadcrumb: true,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_SETTINGS,
    breadcrumb: "Configurações de reservas",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_SETTINGS),
    showBreadcrumb: true,
    breadcrumbParentPath: ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_NEW,
    breadcrumb: "Novo espaço",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_NEW),
    showBreadcrumb: true,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_DETAIL,
    breadcrumb: "Detalhe do espaço",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_DETAIL),
    showBreadcrumb: true,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":meetingRoomId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_EDIT,
    breadcrumb: "Editar espaço",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_EDIT),
    showBreadcrumb: true,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":meetingRoomId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED,
    breadcrumb: "Espaços reservados",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED),
    showBreadcrumb: true,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS,
    breadcrumb: "Gerenciar solicitações",
    component: Page(ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS),
    showBreadcrumb: true,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_LIST,
    breadcrumb: "Programas",
    component: Page(ROUTES.HUB.COMPANY.CHALLENGE_LIST),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.ECOSYSTEM,
    breadcrumb: "Ecossistemas",
    component: Page(ROUTES.HUB.COMPANY.ECOSYSTEM),
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      'caixa_employee',
    ],
  },
  {
    path: ROUTES.HUB.COMPANY.NEW_CHALLENGE,
    breadcrumb: "Criar programa",
    component: Page(ROUTES.HUB.COMPANY.NEW_CHALLENGE),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.REFACTORY_NEW_CHALLENGE,
    breadcrumb: "Criar programa",
    component: <ProgramForm />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.REFACTORY_NEW_CHALLENGE_EDIT,
    breadcrumb: "Editar programa",
    component: <ProgramForm />,
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.GET_CHALLENGE,
    breadcrumb: "Visualizar programa",
    component: Page(ROUTES.HUB.COMPANY.GET_CHALLENGE),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.EDIT_CHALLENGE,
    breadcrumb: "Editar programa",
    component: Page(ROUTES.HUB.COMPANY.EDIT_CHALLENGE),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_EVALUATORS,
    breadcrumb: "Gerenciar avaliadores",
    component: Page(ROUTES.HUB.COMPANY.CHALLENGE_EVALUATORS),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_SOLUTIONS,
    breadcrumb: "Gerenciar inscrições",
    component: Page(ROUTES.HUB.COMPANY.CHALLENGE_SOLUTIONS),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.EVENTS_LIST,
    breadcrumb: "Eventos",
    component: Page(ROUTES.HUB.COMPANY.EVENTS_LIST),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.NEW_EVENT,
    breadcrumb: "Criar Evento",
    component: Page(ROUTES.HUB.COMPANY.NEW_EVENT),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.EDIT_EVENT,
    breadcrumb: "Editar Evento",
    component: Page(ROUTES.HUB.COMPANY.EDIT_EVENT),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":eventId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.GET_EVENT,
    breadcrumb: "Visualizar Evento",
    component: Page(ROUTES.HUB.COMPANY.GET_EVENT),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":eventId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.SCOUTING_LIST,
    breadcrumb: "Scouting de Startups",
    component: Page(ROUTES.HUB.COMPANY.SCOUTING_LIST),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.NEW_SCOUTING,
    breadcrumb: "Criar Desafio",
    component: Page(ROUTES.HUB.COMPANY.NEW_SCOUTING),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.EDIT_SCOUTING,
    breadcrumb: "Editar Desafio",
    component: Page(ROUTES.HUB.COMPANY.NEW_SCOUTING),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":scoutingId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.GET_SCOUTING,
    breadcrumb: "Visualizar Desafio",
    component: Page(ROUTES.HUB.COMPANY.GET_SCOUTING),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":scoutingId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.PUBLIC_ORGANS,
    breadcrumb: "Órgãos públicos",
    component: Page(ROUTES.HUB.COMPANY.PUBLIC_ORGANS),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CITY_HALL_MEMBERS,
    breadcrumb: "Adicionar membros",
    component: Page(ROUTES.HUB.COMPANY.CITY_HALL_MEMBERS),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.CITY_HALL_MAIN,
    breadcrumb: "Painel da Prefeitura",
    component: Page(ROUTES.HUB.COMPANY.CITY_HALL_MAIN),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, USER_FEATURES.CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.CHALLENGE_LIST,
    breadcrumb: "Programas",
    component: Page(ROUTES.HUB.STARTUP.CHALLENGE_LIST),
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.GET_CHALLENGE,
    breadcrumb: "Visualizar programa",
    component: Page(ROUTES.HUB.STARTUP.GET_CHALLENGE),
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.APPLY_CHALLENGE,
    breadcrumb: "Inscrever-se no programa",
    component: Page(ROUTES.HUB.STARTUP.APPLY_CHALLENGE),
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.IDEA_LIST,
    breadcrumb: "Minhas inscrições",
    component: Page(ROUTES.HUB.STARTUP.IDEA_LIST),
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.GET_IDEA,
    breadcrumb: "Inscrição",
    component: Page(ROUTES.HUB.STARTUP.GET_IDEA),
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":ideaId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.EDIT_IDEA,
    breadcrumb: "Inscrição",
    component: Page(ROUTES.HUB.STARTUP.EDIT_IDEA),
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":ideaId", ":challengeId"] },
  },
  {
    path: ROUTES.HUB.STARTUP.EVENTS_LIST,
    breadcrumb: "Eventos",
    component: Page(ROUTES.HUB.STARTUP.EVENTS_LIST),
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.STARTUP.GET_EVENT,
    breadcrumb: "Visualizar Evento",
    component: Page(ROUTES.HUB.STARTUP.GET_EVENT),
    features: [USER_FEATURES.IDEA, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":eventId"] },
  },
  {
    path: ROUTES.HUB.EVALUATOR.CHALLENGE_LIST,
    breadcrumb: "Minhas avaliações",
    component: Page(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.EVALUATOR.EVALUATE_IDEA,
    breadcrumb: "Avaliar ideia",
    component: Page(ROUTES.HUB.EVALUATOR.EVALUATE_IDEA),
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
    props: { params: [":challengeId", ":stepId", ":ideaId"] },
  },
  {
    path: ROUTES.HUB.EVALUATOR.INVITES,
    breadcrumb: "Meus convites",
    component: Page(ROUTES.HUB.EVALUATOR.INVITES),
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.EVALUATOR.GET_CHALLENGE,
    breadcrumb: "Visualizar programa",
    component: Page(ROUTES.HUB.EVALUATOR.GET_CHALLENGE),
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
    props: { params: [":challengeId"] },
  },
  {
    path: ROUTES.HUB.EVALUATOR.CHALLENGE_SOLUTIONS,
    breadcrumb: "Soluções do programa",
    component: Page(ROUTES.HUB.EVALUATOR.CHALLENGE_SOLUTIONS),
    features: [USER_FEATURES.EVALUATION, 'caixa_employee'],
    props: { params: [":challengeId",":stepId"] },
  },
  {
    path: ROUTES.HUB.COMPANY.CHALLENGE_SOLUTIONS_SUBSCRIPTIONS,
    breadcrumb: "Visualizar Inscrição",
    component: Page(ROUTES.HUB.COMPANY.CHALLENGE_SOLUTIONS_SUBSCRIPTIONS),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":challengeId", ":solutionId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_LIST,
    breadcrumb: "Trilhas",
    component: Page(ROUTES.HUB.TRACK.TRACKS_LIST),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_CREATE,
    breadcrumb: "Criar trilha",
    component: Page(ROUTES.HUB.TRACK.TRACKS_CREATE),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_EDIT,
    breadcrumb: "Editar trilha",
    component: Page(ROUTES.HUB.TRACK.TRACKS_EDIT),
    features: [USER_FEATURES.CHALLENGE, 'caixa_employee'],
    props: { params: [":trackId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_AVAILABLE,
    breadcrumb: "Trilhas disponíveis",
    component: Page(ROUTES.HUB.TRACK.TRACKS_AVAILABLE),
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.TRACK.LEARNING,
    breadcrumb: "Meus aprendizados",
    component: Page(ROUTES.HUB.TRACK.LEARNING),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, 'caixa_employee'],
  },
  {
    path: ROUTES.HUB.COMPANY.ECOSYSTEM_PUBLIC_ADMINISTRATION,
    breadcrumb: 'Detalhes do órgão público',
    component: Page(ROUTES.HUB.COMPANY.ECOSYSTEM_PUBLIC_ADMINISTRATION),
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      'caixa_employee',
    ],
    props: { params: [':publicAdministrationId'] }
  },
  {
    path: ROUTES.HUB.COMPANY.ECOSYSTEM_STARTUP,
    breadcrumb: 'Detalhes da startup',
    component: Page(ROUTES.HUB.COMPANY.ECOSYSTEM_STARTUP),
    features: [
      USER_FEATURES.CHALLENGE,
      USER_FEATURES.IDEA,
      USER_FEATURES.CITY_HALL,
      USER_FEATURES.ADMIN_CITY_HALL,
      'caixa_employee',
    ],
    props: { params: [':companyId'] }
  },
  {
    path: ROUTES.HUB.TRACK.LEARNING_LESSON,
    breadcrumb: () => {
      const trackNameElement = document.querySelector('[data-track-name]');
      return trackNameElement?.textContent || '';
    },
    component: Page(ROUTES.HUB.TRACK.LEARNING_LESSON),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, 'caixa_employee'],
    props: { params: [":lessonId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_AVAILABLE_DETAILS,
    breadcrumb: () => {
      const trackNameElement = document.querySelector('[data-track-name]');
      return trackNameElement?.textContent || '';
    },
    component: Page(ROUTES.HUB.TRACK.TRACKS_AVAILABLE_DETAILS),
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":trackId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_AVAILABLE_LESSON,
    breadcrumb: "Aula",
    component: Page(ROUTES.HUB.TRACK.TRACKS_AVAILABLE_LESSON),
    features: [USER_FEATURES.IDEA, USER_FEATURES.CITY_HALL, 'caixa_employee'],
    props: { params: [":lessonId"] },
  },
  {
    path: ROUTES.HUB.TRACK.TRACKS_GET,
    breadcrumb: () => {
      const trackNameElement = document.querySelector('[data-track-name]');
      return trackNameElement?.textContent || '';
    },
    component: Page(ROUTES.HUB.TRACK.TRACKS_GET),
    features: [USER_FEATURES.CHALLENGE, USER_FEATURES.ADMIN_CITY_HALL, 'caixa_employee'],
    props: { params: [":trackId"] },
  },
];
